
// This is the FALLBACK config only — normally the app fetches the live,
// centrally-managed config from DWS (via DDS's getStatsConfig, see
// services/statsConfig.service.js) so AeDrive and Aether always show the
// identical stat list. This hardcoded copy is used only if that fetch
// fails or the backend hasn't been seeded yet, so the panel never renders
// empty. Keep it in sync with BB_AdminPanelUI's statshelper.js
// STATIC_STAT_CONFIG and with BB_DataWorldService's seed-stats-config.js —
// all three should carry the same 15 stats + Cards.
export const STATIC_STAT_CONFIG = [
    { id: 'possessionPercentage', type: 'possessionPercentage', label: 'Possession (%)', sumTo100: true },
    { id: 'possession_last5', type: 'possessionLast5', label: 'Possession (%) Last 5 Mins', sumTo100: true },
    { id: 'possession_last10', type: 'possessionLast10', label: 'Possession (%) Last 10 Mins', sumTo100: true },
    { id: 'possession_2h', type: 'possessionPercentage', label: '2nd Half Possession (%)', valueField: 'second_half_value', sumTo100: true },
    { id: 'wonCorners', type: 'wonCorners', label: 'Corners' },
    { id: 'totalScoringAtt', type: 'totalScoringAtt', label: 'Attempts' },
    { id: 'totalScoringAtt_2h', type: 'totalScoringAtt', label: '2nd Half Attempts', valueField: 'second_half_value' },
    { id: 'ontargetScoringAtt', type: 'ontargetScoringAtt', label: 'Attempts on Target' },
    { id: 'distanceCovered', type: 'distanceRun', label: 'Distance Covered (KM)', divideBy: 1000 },
    { id: 'touchesInOppBox', type: 'touchesInOppBox', label: 'Touches in Opp. Box' },
    { id: 'totalPass', type: 'totalPass', label: 'Passes' },
    { id: 'passAccuracy', label: 'Pass Accuracy (%)', derive: 'passAccuracy', displayType: 'split-percentage' },
    { id: 'accuratePass', type: 'accuratePass', label: 'Completed Passes' },
    { id: 'finalThirdPasses', type: 'totalFinalThirdPasses', label: 'Final Third Passes' },
    { id: 'passesIntoFinalThird', type: 'finalThirdEntries', label: 'Passes into Final Third' },
    // Yellow/Red Cards are sourced from the AeDrive-authoritative *Opus rows
    // written by handleCardAction, not the lagging Opta totals.
    { id: 'totalYellowCard', type: 'totalYellowCardOpus', label: 'Yellow Cards' },
    { id: 'totalRedCard', type: 'totalRedCardOpus', label: 'Red Cards' },
];

const round = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.round(n) : 0; };


const StatFormulas = {
    passAccuracy(statMap) {
        const accurate = Number(statMap.get('accuratePass')?.value ?? 0);
        const total = Number(statMap.get('totalPass')?.value ?? 0);
        if (!total || !Number.isFinite(accurate)) return '0';
        return String(round((accurate / total) * 100));
    },
};


export class TeamStatsBuilder {
    constructor(homeStats = [], awayStats = [], statConfig = STATIC_STAT_CONFIG) {
        // Keep the full row (not just .value) so valueField lookups reach
        // first_half_value / second_half_value.
        this.home = new Map(homeStats.map((s) => [s.type, s]));
        this.away = new Map(awayStats.map((s) => [s.type, s]));
        this.statConfig = statConfig;
    }

    build() {
        return this.statConfig.map((cfg) => this.#row(cfg));
    }

    #row(cfg) {
        let home = this.#value(this.home, cfg);
        let away = this.#value(this.away, cfg);


        if (cfg.sumTo100 && this.#has(this.home, cfg)) {
            const h = round(home);
            home = String(h);
            away = String(100 - h);
        }

        return {
            id: cfg.id,
            key: cfg.id,
            label: cfg.label,
            home,
            away,
            ...(cfg.displayType && { displayType: cfg.displayType }),
        };
    }

    #value(map, cfg) {
        if (cfg.derive) return StatFormulas[cfg.derive](map);
        const row = map.get(cfg.type);
        if (!row) return '0';
        const raw = cfg.valueField ? (row[cfg.valueField] ?? '0') : (row.value ?? '0');
        if (!cfg.divideBy) return raw;
        const n = Number(raw);
        return Number.isFinite(n) ? (n / cfg.divideBy).toFixed(1) : '0';
    }

    #has(map, cfg) {
        const row = map.get(cfg.type);
        return !!row && (cfg.valueField ? row[cfg.valueField] != null : row.value != null);
    }
}

export const buildTeamStats = (homeStats = [], awayStats = [], statConfig = STATIC_STAT_CONFIG) =>
    new TeamStatsBuilder(homeStats, awayStats, statConfig).build();
